import { useState, useCallback } from 'react';
import {
  getAllEnvios,
  getMisEnvios,
  consultarPorGuia,
  getEnviosPorTipo
} from '../services/enviosService';

export function useEnvios(isAdmin, handleError) {
  const [envios, setEnvios] = useState([]);
  const [totalEnvios, setTotalEnvios] = useState(0);
  const [loadingList, setLoadingList] = useState(false);

  const [envioConsultado, setEnvioConsultado] = useState(null);
  const [loadingConsulta, setLoadingConsulta] = useState(false);

  // 🔹 Obtener envíos (admin o cliente) con paginación
  const fetchEnvios = useCallback(async (page = 1, limit = 20) => {
    try {
      setLoadingList(true);
      const res = isAdmin ? await getAllEnvios({ page, limit }) : await getMisEnvios({ page, limit });

      if (!res || res.success === false) {
        const mensaje = res?.mensaje || '❌ Error al cargar envíos';
        if (handleError) handleError(mensaje);
        setEnvios([]);
        setTotalEnvios(0);
        return { success: false, envios: [], total: 0, mensaje };
      }

      setEnvios(res.envios || []);
      setTotalEnvios(res.total || res.envios?.length || 0);

      return { success: true, envios: res.envios || [], total: res.total || 0, mensaje: res.mensaje || '✅ Envíos cargados' };
    } catch (err) {
      const mensaje = err?.message || '❌ Error al cargar envíos';
      if (handleError) handleError(mensaje);
      setEnvios([]);
      setTotalEnvios(0);
      return { success: false, envios: [], total: 0, mensaje };
    } finally {
      setLoadingList(false);
    }
  }, [isAdmin, handleError]);

  // 🔹 Obtener solo los envíos del cliente con paginación
  const fetchMisEnvios = useCallback(async (page = 1, limit = 50) => {
    try {
      setLoadingList(true);
      const res = await getMisEnvios({ page, limit });

      if (!res || res.success === false) {
        const mensaje = res?.mensaje || '❌ Error al cargar mis envíos';
        if (handleError) handleError(mensaje);
        setEnvios([]);
        setTotalEnvios(0);
        return { success: false, envios: [], total: 0, mensaje };
      }

      setEnvios(res.envios || []);
      setTotalEnvios(res.total || res.envios?.length || 0);

      return { success: true, envios: res.envios || [], total: res.total || 0, mensaje: res.mensaje || '✅ Mis envíos cargados' };
    } catch (err) {
      const mensaje = err?.message || '❌ Error al cargar mis envíos';
      if (handleError) handleError(mensaje);
      setEnvios([]);
      setTotalEnvios(0);
      return { success: false, envios: [], total: 0, mensaje };
    } finally {
      setLoadingList(false);
    }
  }, [handleError]);

  // 🔹 Obtener envíos por tipo con paginación (admin/superadmin)
  const fetchEnviosPorTipo = useCallback(async (tipo, page = 1, limit = 20) => {
    try {
      setLoadingList(true);
      const res = await getEnviosPorTipo(tipo, { page, limit });

      if (!res || res.success === false) {
        const mensaje = res?.mensaje || '❌ Error al filtrar envíos';
        if (handleError) handleError(mensaje);
        setEnvios([]);
        setTotalEnvios(0);
        return { success: false, envios: [], total: 0, mensaje };
      }

      setEnvios(res.envios || []);
      setTotalEnvios(res.total || res.envios?.length || 0);

      return { success: true, envios: res.envios || [], total: res.total || 0, mensaje: res.mensaje || '✅ Envíos filtrados' };
    } catch (err) {
      const mensaje = err?.message || '❌ Error al filtrar envíos';
      if (handleError) handleError(mensaje);
      setEnvios([]);
      setTotalEnvios(0);
      return { success: false, envios: [], total: 0, mensaje };
    } finally {
      setLoadingList(false);
    }
  }, [handleError]);

  // 🔹 Consultar envío por guía
  const consultarEnvio = useCallback(async (guia) => {
    try {
      setLoadingConsulta(true);
      const res = await consultarPorGuia(guia);

      if (!res || res.success === false) {
        const mensaje = res?.mensaje || '❌ Error al consultar envío';
        if (handleError) handleError(mensaje);
        setEnvioConsultado(null);
        return { success: false, envio: null, mensaje };
      }

      setEnvioConsultado(res.envio || null);
      return { success: true, envio: res.envio || null, mensaje: res.mensaje || '✅ Envío consultado' };
    } catch (err) {
      const mensaje = err?.message || '❌ Error al consultar envío';
      if (handleError) handleError(mensaje);
      setEnvioConsultado(null);
      return { success: false, envio: null, mensaje };
    } finally {
      setLoadingConsulta(false);
    }
  }, [handleError]);

  return {
    envios,
    totalEnvios,
    loadingList,
    fetchEnvios,
    fetchMisEnvios,
    fetchEnviosPorTipo,
    envioConsultado,
    consultarEnvio,
    loadingConsulta,
    setEnvioConsultado,
    setEnvios
  };
}